import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { DataContext } from "../../utils/DataContext";

import classes from "../../components/Modal.module.css";

import SectionDivider from "../../components/SectionDivider";
import Dropdown from "../../components/Dropdown";
import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/CustomButton";
import AddAnotherButton, { GENDER } from "../../components/AddAnotherButton";
import HotDropdown from "../../components/HotDropdown";

import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import addIcon from "../../assets/add_icon.png";
import AreYouSureModal from "../../components/AreYouSureModal";
import PortalDropdown from "../../components/PortalDropdown";

export default function BankAccountSection({
  sendDataToParent,
  data,
  deleteUrl = "",
  refetchFunction = () => {},
  isIraqi = true,
}) {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { error, setError, currencies } = useContext(DataContext);

  const [banks, setBanks] = useState([]);

  const [page, setPage] = useState(1);

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [accountIndex, setAccountIndex] = useState(null);

  const [bankAccounts, setBankAccounts] = useState([
    {
      selectedBank: {
        title: t("chooseABank"),
        value: "",
      },
      selectedBranch: {
        title: t("chooseABranch"),
        value: "",
      },
      branches: [],
      accountNumber: "",
      accountCurrency: {
        title: t("chooseAccountCurrency"),
        value: "",
      },
      swiftCode: "",
      iban: "",
    },
  ]);

  // For Edit (SubCompany | Supplier)
  useEffect(() => {
    if (data && Array.isArray(data) && data.length > 0) {
      const getAllBanksReq = async () => {
        try {
          const res = await getAllBanks(1, 5, true, "");
          const allBanks = res?.data?.banks || [];

          const formattedData = data.map((bankAccount) => {
            const matchingBank = allBanks.find(
              (bank) => bank.id === bankAccount?.bankId
            );

            // Get branches for the matching bank if found
            const branches = matchingBank?.branch || [];

            return {
              id: bankAccount?.id,
              selectedBank: {
                title: bankAccount?.bank?.name || t("chooseABank"),
                value: bankAccount?.bank?.id || "",
              },
              selectedBranch: {
                title: bankAccount.branch?.name || t("chooseABranch"),
                value: bankAccount.branch?.id || "",
                address: bankAccount.branch?.address || "",
              },
              branches, // Branches from the matching bank
              accountNumber: bankAccount.accountNumber || "",
              accountCurrency: {
                title:
                  currencies.find(
                    (c) => c.value === bankAccount.accountCurrency
                  )?.title || t("chooseAccountCurrency"),
                value: bankAccount.accountCurrency || "",
              },
              swiftCode: bankAccount.swiftCode || "",
              iban: bankAccount.iban || "",
            };
          });

          setBankAccounts(formattedData);
        } catch (error) {
          console.error("Error fetching banks:", error);
        }
      };

      // Call the function to fetch banks and process data
      getAllBanksReq();
    }
  }, [data]);

  const spanStyles = currentDirection === "rtl" ? classes.shortSpan : null;
  const emptyInputField = error === "emptyInputFields" ? "emptyInputField" : "";

  const getAllBanks = async (
    page,
    limit,
    isDropdown = true, // * It's important to make the Hotdropdown working correctly
    searchQuery
  ) => {
    const organizationId = userData?.organizationId;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/bank`,
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
            "organization-id": isIraqi ? null : organizationId,
          },
          params: { page, limit, searchQuery, isIraqi: isIraqi },
        }
      );
      if (response.status === 200) {
        setBanks(response.data?.banks);
        return response; // Ensure HotDropdown receives a response
      }
    } catch (error) {
      setError(error?.response?.message || "Failed to fetch banks");
      console.error("Failed to fetch banks", error);
    }
  };

  const handleSelectBank = (bank, index) => {
    const { id, name, branch: branches } = bank;

    setBankAccounts((prev) => {
      const updatedAccounts = [...prev];
      updatedAccounts[index] = {
        ...updatedAccounts[index],
        selectedBank: { title: name, value: id },
        selectedBranch: { title: t("chooseABranch"), value: "" },
        branches,
      };
      return updatedAccounts;
    });
  };

  const handleSelectBranch = (branch, index) => {
    const { id, name, address } = branch;

    setBankAccounts((prev) => {
      const updatedAccounts = [...prev];
      updatedAccounts[index] = {
        ...updatedAccounts[index],
        selectedBranch: { title: name, value: id, address },
      };
      return updatedAccounts;
    });
  };

  const handleAccountCurrency = (currency, index) => {
    const { title, value } = currency;

    setBankAccounts((prev) => {
      const updatedAccounts = [...prev];
      updatedAccounts[index] = {
        ...updatedAccounts[index],
        accountCurrency: { title, value },
      };
      return updatedAccounts;
    });
  };

  const handleInputChange = (e, pageIndex) => {
    const { name, value } = e.target;

    setBankAccounts((prevAccounts) =>
      prevAccounts.map((item, index) => {
        // Keep other items unchanged
        if (index !== pageIndex) return item;

        // Update the target item
        return { ...item, [name]: value };
      })
    );
  };

  const handleAddInput = (e) => {
    e.preventDefault();
    const newBankAccount = {
      selectedBank: {
        title: t("chooseABank"),
        value: "",
      },
      selectedBranch: {
        title: t("chooseABranch"),
        value: "",
      },
      branches: [],
      accountNumber: "",
      accountCurrency: {
        title: t("chooseAccountCurrency"),
        value: "",
      },
      swiftCode: "",
      iban: "",
    };

    setBankAccounts((prevAccounts) => [...prevAccounts, newBankAccount]);

    setPage((prevPage) => prevPage + 1);
    setBanks([]);
  };

  const handleNext = () => {
    if (page < bankAccounts.length) setPage(page + 1);
  };

  const handlePrevious = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleShowConfirmDelete = () => {
    setShowConfirmDelete(true);
  };

  const handleDeleteBankAccount = async (indexToDelete) => {
    const bankAccountToDelete = bankAccounts[indexToDelete];

    // If the bank account has an ID, delete it from backend
    if (bankAccountToDelete.id) {
      try {
        const res = await axios.patch(
          `${process.env.REACT_APP_URL}/${deleteUrl}/${bankAccountToDelete.id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${userData?.token}`,
              "organization-Id": parseInt(userData?.organizationId),
            },
          }
        );

        if (res.status === 200) {
          setShowConfirmDelete(false);
          setAccountIndex(null);
          await refetchFunction();
        }
      } catch (error) {
        console.error("Error deleting bank account:", error);
        setError(
          error?.response?.data?.message || "Failed to delete bank account"
        );
        setShowConfirmDelete(false);
        return;
      }
    }

    // Remove from frontend state
    setBankAccounts((prev) => {
      const updated = prev.filter((_, index) => index !== indexToDelete);
      if (updated.length === 0) {
        return [
          {
            selectedBank: { title: t("chooseABank"), value: "" },
            selectedBranch: { title: t("chooseABranch"), value: "" },
            branches: [],
            accountNumber: "",
            accountCurrency: { title: t("chooseAccountCurrency"), value: "" },
            swiftCode: "",
            iban: "",
          },
        ];
      }
      return updated;
    });

    // Adjust page if needed
    if (page > 1 && indexToDelete + 1 === page) {
      setPage((prev) => prev - 1);
    } else if (page > bankAccounts.length - 1) {
      setPage(bankAccounts.length - 1 || 1);
    }

    setShowConfirmDelete(false);
    setAccountIndex(null);
  };

  useEffect(() => {
    const payload = bankAccounts
      .map((bankAccount) => {
        if (!bankAccount.selectedBank?.value) return null;

        return {
          bank: {
            name: bankAccount.selectedBank?.title,
            id: bankAccount.selectedBank?.value,
          },
          branch: {
            name: bankAccount.selectedBranch?.title,
            id: bankAccount.selectedBranch?.value,
            address: bankAccount?.selectedBranch?.address,
          },
          accountNumber: bankAccount.accountNumber,
          accountCurrency: bankAccount.accountCurrency?.value,
          swiftCode: bankAccount.swiftCode,
          iban: bankAccount.iban,
          id: bankAccount.id,
        };
      })
      .filter((account) => account !== null);

    sendDataToParent(payload);
  }, [bankAccounts]);

  return (
    <>
      <SectionDivider title="bankAccountInfo" />

      <div
        className={`add-shipped-container-container-part ${classes.flexableForm} ${classes.flexableFormWithPadding}`}
      >
        {bankAccounts?.map((item, index) => {
          if (index + 1 === page) {
            return (
              <>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("bankName")}: </span>
                  <HotDropdown
                    initOptions={[...banks]}
                    selected={item?.selectedBank}
                    setSelected={(selected) =>
                      handleSelectBank(selected, index)
                    }
                    thick
                    flex
                    getData={getAllBanks}
                    optionsName={"banks"}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("branch")}: </span>
                  <Dropdown
                    options={item?.branches}
                    selected={item?.selectedBranch}
                    setSelected={(selected) =>
                      handleSelectBranch(selected, index)
                    }
                    thick
                    flex
                    errorText={emptyInputField}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("accountNumber")}: </span>
                  <CustomInput
                    name="accountNumber"
                    value={item?.accountNumber}
                    onChange={(e) => handleInputChange(e, index)}
                    flex={true}
                    errorText={emptyInputField}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("accountCurrency")}: </span>
                  <PortalDropdown
                    options={currencies}
                    selected={item?.accountCurrency}
                    setSelected={(selected) =>
                      handleAccountCurrency(selected, index)
                    }
                    thick
                    flex
                    errorText={emptyInputField}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("SWIFT")}: </span>
                  <CustomInput
                    name="swiftCode"
                    value={item?.swiftCode}
                    onChange={(e) => handleInputChange(e, index)}
                    flex={true}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("IBAN")}: </span>
                  <CustomInput
                    name="iban"
                    value={item?.iban}
                    onChange={(e) => handleInputChange(e, index)}
                    flex={true}
                  />
                </div>

                {bankAccounts[0].selectedBank.value === "" ? null : (
                  <div className="u-width_full-width o-row o-column--vertical-align_center o-row--horizontal-align_end">
                    <button
                      onClick={() => {
                        setAccountIndex(index);
                        handleShowConfirmDelete();
                      }}
                      className="temp-delete-button"
                    >
                      ❌ {t("delete")}
                    </button>
                  </div>
                )}
              </>
            );
          }
          return null;
        })}
      </div>

      <div>
        <div className={""}>
          {bankAccounts?.length ? (
            <div className={"add-shipped-container-next-previous"}>
              <CustomButton
                variant="blank"
                title={t("previous")}
                onClick={handlePrevious}
                disabled={page === 1}
                icon={currentDirection === "ltr" ? previousIcon : nextIcon}
                iconSize={15}
              />
              <CustomButton
                title={t("next")}
                variant="blank"
                onClick={handleNext}
                disabled={page === bankAccounts.length}
                // disabled={page === mainState.bankAccounts.length}
                icon={currentDirection === "ltr" ? nextIcon : previousIcon}
                iconSize={15}
                reverseIcon
              />
            </div>
          ) : null}
        </div>

        <div className={"add-shipped-container-add-another"}>
          <AddAnotherButton
            onClick={handleAddInput}
            variant="blank"
            title={t("addBankAccount")}
            icon={addIcon}
            iconSize={16}
            gender={GENDER.MALE}
            arrayLength={bankAccounts?.length}
          />
        </div>

        {showConfirmDelete ? (
          <AreYouSureModal
            // shouldCloseModal={shouldCloseModal({
            //   invoiceHeaderImage,
            //   invoiceFooterImage,
            // })}
            customMessage={t("deleteBankAccount")}
            onClose={() => setShowConfirmDelete(false)}
            onConfirm={() => handleDeleteBankAccount(accountIndex)}
            closeTitle={t("noDontDelete")}
            confirmTitle={t("yesDelete")}
            confirmIconType="whiteCheck"

          />
        ) : null}
      </div>
    </>
  );
}
